/**
 * FikoYT Advanced Dimension System - Index
 *
 * Gelişmiş 3D ölçüm ve görselleştirme sistemi
 * Tüm advanced bileşenleri dışa aktarır
 *
 * @author FikoYT Advanced CAD Team
 * @version 2.0.0
 */

import * as THREE from 'three';

// ============================================================================
// IMPORTS FOR FACTORY FUNCTIONS
// ============================================================================
import { FikoAdvancedDimension, AdvancedDimensionType, DimensionTheme, AnimationType, AdvancedArrowStyle, AdvancedLineStyle, LabelStyle, THEME_PALETTES, DEFAULT_ADVANCED_OPTIONS } from './FikoAdvancedDimension';
import { FikoInteractiveArrow, ArrowStyleType, ArrowAnimation, ArrowState, ArrowDirection, DEFAULT_ARROW_COLORS, DEFAULT_ARROW_OPTIONS } from './FikoInteractiveArrow';
import { FikoMeasurementArc, ArcStyleType, AngleUnit, ArcType, ArcAnimationType, ArcState, ARC_THEMES, DEFAULT_ARC_CONFIG } from './FikoMeasurementArc';
import { FikoSmartLine, LineType, LineStyleType, LineAnimationType, LineCapType, LineJoinType, LineState, ControlPointType, LINE_THEMES, DEFAULT_LINE_CONFIG } from './FikoSmartLine';
import { FikoAnnotation3D, AnnotationStyle, AnnotationPosition, LeaderLineType, AnnotationAnimation, AnnotationState, ContentType, ANNOTATION_THEMES, DEFAULT_ANNOTATION_CONFIG, STYLE_TEMPLATES } from './FikoAnnotation3D';
import { FikoDimensionEffects, EffectType, EasingType, BaseEffect, GlowEffect, PulseEffect, TrailEffect, ParticleEffect, HolographicEffect, EnergyFieldEffect, OutlineEffect, DEFAULT_EFFECT_CONFIG, EASING_FUNCTIONS } from './FikoDimensionEffects';
import { FikoCatiaDimension, CatiaDimensionManager, CatiaDimensionType, ToleranceType, CatiaArrowStyle, TextPosition, CATIA_DEFAULT_STYLE, DEFAULT_TOLERANCE, CATIA_STYLE } from './FikoCatiaDimension';
import { FikoGDTSymbol, FikoDatumReference, FikoGDTManager, GDTSymbolType, MaterialCondition, GDT_SYMBOLS, DEFAULT_GDT_STYLE, CATIA_GDT_STYLE, createFlatnessSymbol, createPerpendicularitySymbol, createParallelismSymbol, createPositionSymbol } from './FikoGDTSymbol';
import { FikoInteractiveDimension, DimensionSegment, ArrowType, LineType as InteractiveLineType, LabelPosition, DimensionPreset, DIMENSION_PRESETS, DEFAULT_INTERACTIVE_CONFIG, createCatiaDimension, createSolidWorksDimension, createMinimalDimension, createTechnicalDimension } from './FikoInteractiveDimension';
import { FikoMeasurementDisplay, MeasurementType, DisplayStyle, DEFAULT_DISPLAY_CONFIG, createDistanceDisplay, createDiameterDisplay, createRadiusDisplay, createAngleDisplay, createCoordinateDisplay, createAreaDisplay } from './FikoMeasurementDisplay';
import { FikoDimensionInteraction, DEFAULT_INTERACTION_CONFIG, createDimensionInteraction, createSnapInteraction, createBoundedInteraction } from './FikoDimensionInteraction';

// ============================================================================
// ADVANCED DIMENSION
// ============================================================================

export {
    FikoAdvancedDimension,
    AdvancedDimensionType,
    DimensionTheme,
    AnimationType,
    AdvancedArrowStyle,
    AdvancedLineStyle,
    LabelStyle,
    THEME_PALETTES,
    DEFAULT_ADVANCED_OPTIONS
} from './FikoAdvancedDimension';

export type {
    AdvancedDimensionOptions,
    ThemeColorPalette
} from './FikoAdvancedDimension';

// ============================================================================
// INTERACTIVE ARROW
// ============================================================================

export {
    FikoInteractiveArrow,
    ArrowStyleType,
    ArrowAnimation,
    ArrowState,
    ArrowDirection,
    DEFAULT_ARROW_COLORS,
    DEFAULT_ARROW_OPTIONS
} from './FikoInteractiveArrow';

export type {
    ArrowOptions,
    ArrowColorPalette
} from './FikoInteractiveArrow';

// ============================================================================
// MEASUREMENT ARC
// ============================================================================

export {
    FikoMeasurementArc,
    ArcStyleType,
    AngleUnit,
    ArcType,
    ArcAnimationType,
    ArcState,
    ARC_THEMES,
    DEFAULT_ARC_CONFIG
} from './FikoMeasurementArc';

export type {
    ArcConfig,
    ArcThemeColors
} from './FikoMeasurementArc';

// ============================================================================
// SMART LINE
// ============================================================================

export {
    FikoSmartLine,
    LineType,
    LineStyleType,
    LineAnimationType,
    LineCapType,
    LineJoinType,
    LineState,
    ControlPointType,
    LINE_THEMES,
    DEFAULT_LINE_CONFIG
} from './FikoSmartLine';

export type {
    SmartLineConfig,
    ControlPoint,
    LineThemeColors
} from './FikoSmartLine';

// ============================================================================
// ANNOTATION 3D
// ============================================================================

export {
    FikoAnnotation3D,
    AnnotationStyle,
    AnnotationPosition,
    LeaderLineType,
    AnnotationAnimation,
    AnnotationState,
    ContentType,
    ANNOTATION_THEMES,
    DEFAULT_ANNOTATION_CONFIG,
    STYLE_TEMPLATES
} from './FikoAnnotation3D';

export type {
    AnnotationConfig,
    AnnotationTheme
} from './FikoAnnotation3D';

// ============================================================================
// DIMENSION EFFECTS
// ============================================================================

export {
    FikoDimensionEffects,
    EffectType,
    EasingType,
    BaseEffect,
    GlowEffect,
    PulseEffect,
    TrailEffect,
    ParticleEffect,
    HolographicEffect,
    EnergyFieldEffect,
    OutlineEffect,
    DEFAULT_EFFECT_CONFIG,
    EASING_FUNCTIONS
} from './FikoDimensionEffects';

export type {
    EffectConfig,
    EffectState
} from './FikoDimensionEffects';

// ============================================================================
// COMBINED TYPES
// ============================================================================

/**
 * Tüm tema isimleri
 */
export type AllThemeNames =
    | 'NEON'
    | 'HOLOGRAPHIC'
    | 'CATIA'
    | 'CATIA_PRO'
    | 'SOLIDWORKS'
    | 'AUTOCAD'
    | 'BLUEPRINT'
    | 'MATRIX'
    | 'FIRE'
    | 'ICE'
    | 'FROST'
    | 'GOLDEN'
    | 'DARK'
    | 'LIGHT'
    | 'MINIMAL'
    | 'CUSTOM';

/**
 * Tüm animasyon tipleri
 */
export type AllAnimationTypes =
    | ArrowAnimation
    | ArcAnimationType
    | LineAnimationType
    | AnnotationAnimation
    | AnimationType;

/**
 * Tüm state tipleri
 */
export type AllStateTypes =
    | ArrowState
    | ArcState
    | LineState
    | AnnotationState;

// ============================================================================
// FACTORY FUNCTIONS
// ============================================================================

/**
 * Hızlı ölçü çizgisi oluştur
 */
export function createQuickDimension(
    start: THREE.Vector3,
    end: THREE.Vector3,
    scene: any, // FikoSahne
    theme: DimensionTheme = DimensionTheme.NEON
): FikoAdvancedDimension {
    const dimension = new FikoAdvancedDimension(scene, start, end, {
        theme
    });
    return dimension;
}

/**
 * Hızlı ok oluştur
 */
export function createQuickArrow(
    style: ArrowStyleType = ArrowStyleType.FILLED_TRIANGLE
): FikoInteractiveArrow {
    const arrow = new FikoInteractiveArrow({
        style
    });
    return arrow;
}

/**
 * Hızlı açı ölçümü oluştur
 */
export function createQuickAngle(
    center: THREE.Vector3,
    point1: THREE.Vector3,
    point2: THREE.Vector3,
    themeName: string = 'NEON'
): FikoMeasurementArc {
    const arc = FikoMeasurementArc.createAngleMeasurement(center, point1, point2);
    arc.applyTheme(themeName);
    return arc;
}

/**
 * Hızlı çizgi oluştur
 */
export function createQuickLine(
    points: THREE.Vector3[],
    type: LineType = LineType.STRAIGHT,
    themeName: string = 'NEON'
): FikoSmartLine {
    const line = new FikoSmartLine({
        points,
        type
    });
    line.applyTheme(themeName);
    return line;
}

/**
 * Hızlı etiket oluştur
 */
export function createQuickLabel(
    text: string,
    position: THREE.Vector3,
    style: AnnotationStyle = AnnotationStyle.BUBBLE,
    themeName: string = 'NEON'
): FikoAnnotation3D {
    const annotation = new FikoAnnotation3D({
        text,
        position,
        style
    });
    annotation.applyTheme(themeName);
    return annotation;
}

/**
 * Hızlı efekt uygula
 */
export function applyQuickEffect(
    target: THREE.Object3D,
    effectType: EffectType,
    color: number = 0x00ff88
): BaseEffect {
    const effectManager = new FikoDimensionEffects();
    const effect = effectManager.createEffect(effectType, { color });
    effectManager.addEffect('quick-effect', effect, target);
    return effect;
}

// ============================================================================
// PRESET CONFIGURATIONS
// ============================================================================

/**
 * CAD yazılımı preset'leri
 */
export const CAD_PRESETS = {
    CATIA: {
        dimension: {
            theme: DimensionTheme.CATIA_PRO,
            arrowStyle: AdvancedArrowStyle.CATIA,
            lineStyle: AdvancedLineStyle.SOLID
        },
        arrow: {
            style: ArrowStyleType.CATIA_STANDARD
        },
        annotation: {
            style: AnnotationStyle.CATIA_STYLE
        }
    },
    SOLIDWORKS: {
        dimension: {
            theme: DimensionTheme.CATIA_PRO,
            arrowStyle: AdvancedArrowStyle.SOLIDWORKS
        },
        arrow: {
            style: ArrowStyleType.SOLIDWORKS_STANDARD
        },
        annotation: {
            style: AnnotationStyle.SOLIDWORKS_STYLE
        }
    },
    AUTOCAD: {
        dimension: {
            lineStyle: AdvancedLineStyle.SOLID
        },
        arrow: {
            style: ArrowStyleType.AUTOCAD_STANDARD
        },
        annotation: {
            style: AnnotationStyle.AUTOCAD_STYLE
        }
    }
};

/**
 * Görsel efekt preset'leri
 */
export const VISUAL_PRESETS = {
    HOLOGRAPHIC: {
        dimension: {
            theme: DimensionTheme.HOLOGRAPHIC,
            animation: AnimationType.GLOW
        },
        effect: EffectType.HOLOGRAPHIC_SCAN
    },
    NEON: {
        dimension: {
            theme: DimensionTheme.NEON,
            animation: AnimationType.GLOW
        },
        effect: EffectType.GLOW_PULSATING
    },
    MATRIX: {
        dimension: {
            theme: DimensionTheme.MATRIX
        },
        effect: EffectType.PARTICLE_ENERGY
    },
    BLUEPRINT: {
        dimension: {
            theme: DimensionTheme.BLUEPRINT
        },
        annotation: {
            style: AnnotationStyle.FLOATING
        }
    }
};

// ============================================================================
// VERSION INFO
// ============================================================================

export const ADVANCED_DIMENSION_VERSION = {
    major: 2,
    minor: 0,
    patch: 0,
    build: 'stable',
    date: '2024-01-18',
    author: 'FikoYT Advanced CAD Team'
};

// ============================================================================
// CATIA DIMENSION
// ============================================================================

export {
    FikoCatiaDimension,
    CatiaDimensionManager,
    CatiaDimensionType,
    ToleranceType,
    CatiaArrowStyle,
    TextPosition,
    CATIA_DEFAULT_STYLE,
    DEFAULT_TOLERANCE,
    CATIA_STYLE
} from './FikoCatiaDimension';

export type {
    ToleranceConfig,
    CatiaStyleConfig,
    CatiaDimensionOptions
} from './FikoCatiaDimension';

// ============================================================================
// GD&T SYMBOLS
// ============================================================================

export {
    FikoGDTSymbol,
    FikoDatumReference,
    FikoGDTManager,
    GDTSymbolType,
    MaterialCondition,
    GDT_SYMBOLS,
    DEFAULT_GDT_STYLE,
    CATIA_GDT_STYLE,
    createFlatnessSymbol,
    createPerpendicularitySymbol,
    createParallelismSymbol,
    createPositionSymbol
} from './FikoGDTSymbol';

export type {
    GDTStyleConfig,
    FCFConfig,
    DatumConfig,
    InteractiveConfig as GDTInteractiveConfig
} from './FikoGDTSymbol';

// ============================================================================
// INTERACTIVE DIMENSION (FULL CUSTOMIZATION)
// ============================================================================

export {
    FikoInteractiveDimension,
    DimensionSegment,
    ArrowType,
    LineType as InteractiveLineType,
    LabelPosition,
    DimensionPreset,
    DIMENSION_PRESETS,
    DEFAULT_INTERACTIVE_CONFIG,
    createCatiaDimension,
    createSolidWorksDimension,
    createMinimalDimension,
    createTechnicalDimension
} from './FikoInteractiveDimension';

export type {
    SegmentStyle,
    ArrowStyle as InteractiveArrowStyle,
    LabelStyle as InteractiveLabelStyle,
    ExtensionLineStyle,
    InteractiveDimensionConfig,
    SegmentState
} from './FikoInteractiveDimension';

// ============================================================================
// MEASUREMENT DISPLAY (KUMPAS/CETVEL ENTEGRASYONU)
// ============================================================================

export {
    FikoMeasurementDisplay,
    MeasurementType,
    DisplayStyle,
    DEFAULT_DISPLAY_CONFIG,
    createDistanceDisplay,
    createDiameterDisplay,
    createRadiusDisplay,
    createAngleDisplay,
    createCoordinateDisplay,
    createAreaDisplay
} from './FikoMeasurementDisplay';

export type {
    MeasurementResult,
    MeasurementDisplayConfig
} from './FikoMeasurementDisplay';

// ============================================================================
// DIMENSION INTERACTION (SÜRÜKLE-BIRAK)
// ============================================================================

export {
    FikoDimensionInteraction,
    DEFAULT_INTERACTION_CONFIG,
    createDimensionInteraction,
    createSnapInteraction,
    createBoundedInteraction
} from './FikoDimensionInteraction';

export type {
    DraggableObject,
    InteractionConfig,
    DragState,
    InteractionEvent
} from './FikoDimensionInteraction';

